import { SentenceRecordingType } from 'types/types'

const PLAYING = 'playing'
const PAUSED = 'paused'
const DEFAULT_PITCH_SHIFT = '1.0'

type PitchShiftType = string

//
// wrapper around an HTML audio media element.
//
interface HTMLAudioWrapper {
  audio: HTMLAudioElement | null
  playStatus: string | null
  blobUrl: string | null
}

//
// Holds one sentence or segments worth of HTMLAudioWrapper for a certain pitch
// shift
//
interface PitchedSentenceOrSegmentAudio {
  sentenceHTMLAudioWrapper: HTMLAudioWrapper
  segmentHTMLAudioWrappers: Array<HTMLAudioWrapper>
  pitchShift: PitchShiftType
}

//
// Holds a sentenceRecording, all its pitch shifts and other data associated
// with it
//
interface SentenceRecordingMetadataWithPitchedAudios {
  sentenceRecording: SentenceRecordingType | null
  audio: { [key: string]: PitchedSentenceOrSegmentAudio }
  downloading: boolean
  updating: boolean
}

function createBlobUrlFromBytes(bytes: Uint8Array): string {
  const blob = new Blob([bytes as BlobPart], { type: 'audio/wav' })
  const blobUrl = window.URL.createObjectURL(blob)
  return blobUrl
}

export interface SoundLevelDataPoint {
  x: number
  y: number
}

function addSoundLevel(dataArray: Array<SoundLevelDataPoint>, level: number, maxLength: number, startTime: Date | null = null): void {
  const now = new Date()
  const baseTime = startTime || now

  dataArray.push({
    x: (now.getTime() - baseTime.getTime()) / 1000,
    y: level,
  })

  while (dataArray.length > maxLength) {
    dataArray.splice(0, 1)
  }
}

/** Browser-safe MIME-type → file extension. Replaces the Node.js `mime-types` package. */
const MIME_TO_EXT: Record<string, string> = {
  'audio/aac': 'aac',
  'audio/flac': 'flac',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/wave': 'wav',
  'audio/webm': 'webm',
  'audio/x-wav': 'wav',
}

function mimeToExtension(mimeType: string): string {
  // Strip codec params (e.g. "audio/webm;codecs=opus" → "audio/webm")
  const base = mimeType.split(';')[0].trim().toLowerCase()
  return MIME_TO_EXT[base] ?? 'bin'
}

export {
  addSoundLevel,
  createBlobUrlFromBytes,
  DEFAULT_PITCH_SHIFT,
  HTMLAudioWrapper,
  mimeToExtension,
  PAUSED,
  PitchedSentenceOrSegmentAudio,
  PitchShiftType,
  PLAYING,
  SentenceRecordingMetadataWithPitchedAudios,
}
